// PR-MAY16-INVESTOR-RATES-V2 item 5 — pending rate-conflict queue.
//
// Top-level page (route /investor-rate-conflicts) listing every open
// conflict from migration 079 and from future operator-induced
// overrides. Each row offers a "resolve" inline form: pick one of the
// distinct rates as the new default → POST to the resolve endpoint.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import {
  useInvestorRateConflicts,
  useResolveInvestorRateConflict,
  type InvestorRateConflictRow,
} from '@/hooks/useInvestorRateConflicts';
import { extractErrorMessage } from '@/lib/api';

export default function RateConflictsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const { data: rows, isLoading, isError, error } = useInvestorRateConflicts();

  return (
    <div className="min-h-screen w-full bg-background flex flex-col" data-rate-conflicts-page>
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8">
        <Link to="/investors" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />
          {t('investors.conflicts.back')}
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="h-7 w-7 text-amber-500" />
          <h1 className="font-display text-3xl text-foreground">{t('investors.conflicts.title')}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{t('investors.conflicts.subtitle')}</p>

        {isLoading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <p className="text-destructive">{extractErrorMessage(error)}</p>
          </div>
        ) : (rows ?? []).length === 0 ? (
          <div className="py-16 text-center bg-card border border-border rounded-lg">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3 opacity-60" />
            <h3 className="font-display text-xl text-foreground">{t('investors.conflicts.empty')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('investors.conflicts.emptyDescription')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {(rows ?? []).map((row) => (
              <ConflictRow key={row.id} row={row} />
            ))}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground mt-12">
          {user?.fullNameEn ?? ''} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>
    </div>
  );
}

function ConflictRow({ row }: { row: InvestorRateConflictRow }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const resolveMut = useResolveInvestorRateConflict();

  const handleResolve = () => {
    setError(null);
    const v = Number(picked);
    if (!Number.isFinite(v) || v <= 0) {
      setError(t('investors.conflicts.errors.pickRate'));
      return;
    }
    resolveMut.mutate({ id: row.id, resolvedRate: v }, {
      onError: (err) => setError(extractErrorMessage(err)),
    });
  };

  return (
    <div className="bg-card border border-amber-500/30 rounded-lg p-4" data-rate-conflict-row={row.id}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-foreground">{row.investor_name}</div>
          <div className="text-xs text-muted-foreground">
            {row.site_name} · {row.effective_from}
          </div>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {t('investors.conflicts.vehicleCount', { count: row.vehicle_count })}
        </span>
      </div>

      <div className="space-y-1 mb-3">
        {row.distinct_rates.map((d, idx) => (
          <label key={idx} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 rounded px-2 py-1">
            <input
              type="radio"
              name={`conflict-${row.id}`}
              value={String(d.rate)}
              checked={picked === String(d.rate)}
              onChange={(e) => setPicked(e.target.value)}
              data-conflict-rate-option={d.rate}
            />
            <span className="tabular-nums font-medium">
              {Number(d.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR
            </span>
            <span className="text-xs text-muted-foreground">
              {t('investors.conflicts.heldBy', { count: d.count })}
            </span>
          </label>
        ))}
      </div>

      {error && <p className="text-xs text-destructive mb-2" role="alert">{error}</p>}

      <Button size="sm" variant="gold" onClick={handleResolve} disabled={resolveMut.isPending || !picked} data-conflict-resolve>
        {resolveMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {t('investors.conflicts.resolve')}
      </Button>
    </div>
  );
}
